import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  DashboardCard,
  DashboardGrid,
  EmptyState,
  Input,
  Label,
  PageHeader,
  SectionHeader,
  Separator,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableShell,
  cn,
} from '@zafroi/ui-kit';
import type { ChatbotTabId } from './BotHealthCard';
import {
  Bot,
  BookCheck,
  BookOpen,
  Code2,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  HelpCircle,
  Lightbulb,
  MessagesSquare,
  PenLine,
  Plus,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
  Workflow,
} from 'lucide-react';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { BotActivitySection } from './BotActivitySection';
import { CustomerChatWidget } from './CustomerChatWidget';
import { BotHealthCard } from './BotHealthCard';
import { UpgradeModal } from './UpgradeModal';
import { UsageBanner } from './UsageBanner';
import { UsageStrip } from './UsageStrip';
import {
  ONBOARDING_PROMPTS,
  type OnboardingPrompt,
} from './onboardingPrompts';
import {
  useBotKnowledge,
  useBotUsage,
  useCustomers,
} from '@/hooks/useEngageQueries';
import { useTenant } from '@/services/tenant-context';
import {
  approveBotKnowledge,
  createBotKnowledge,
  deleteBotKnowledge,
  getBotDomains,
  getBotGaps,
  previewBot,
  updateBotKnowledge,
} from '@/services/engage-api';
import { LinkEditor } from './LinkEditor';
import { AppearanceSection } from './AppearanceSection';
import { botKnowledgeStatusVariants } from '@/types/status-variants';
import { relativeTime } from '@/lib/format';
import type {
  BotDomains,
  BotKnowledge,
  BotKnowledgeGap,
  BotKnowledgeLink,
  BotPreviewReply,
  Customer,
} from '@/types/engage';

/**
 * Pre-fills the Add-knowledge form. Lifted to `ChatbotPage` so the Knowledge
 * gaps section can pre-fill the form the Knowledge base section owns.
 */
interface KnowledgeDraft {
  title: string;
  category: string;
}

// ─── Tab definitions ────────────────────────────────────────────────────────

/**
 * Static metadata for the five tabs the page renders. Co-located so the
 * tab strip, the URL-hash mapping, and the default-tab logic all reference
 * a single source of truth.
 */
const TAB_DEFS: ReadonlyArray<{
  id: ChatbotTabId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'setup', label: 'Setup', icon: ClipboardList },
  { id: 'knowledge', label: 'Knowledge', icon: BookCheck },
  { id: 'insights', label: 'Insights', icon: Lightbulb },
  { id: 'test', label: 'Test', icon: MessagesSquare },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const TAB_IDS = new Set<ChatbotTabId>(TAB_DEFS.map((t) => t.id));

/**
 * Maps a URL hash (without `#`) to the tab id it should activate. Preserves
 * the legacy `#knowledge` / `#gaps` deep-links the Setup Guide page links to.
 */
function hashToTab(hash: string): ChatbotTabId | null {
  const raw = hash.replace(/^#/, '');
  if (!raw) return null;
  switch (raw) {
    case 'knowledge':
      return 'knowledge';
    case 'gaps':
      return 'insights';
    case 'setup':
      return 'setup';
    case 'test':
      return 'test';
    case 'settings':
      return 'settings';
    default:
      // Unknown hashes don't override — fall through to default selection.
      return TAB_IDS.has(raw as ChatbotTabId) ? (raw as ChatbotTabId) : null;
  }
}

export function ChatbotPage() {
  const [botName, setBotName] = useState('Zara');
  const [greeting, setGreeting] = useState(
    "Hey! I'm Zara — how can we help today?",
  );

  // The Add-knowledge form's title/category live here so the Knowledge gaps
  // section can pre-fill them when "Write the answer" is clicked.
  const [knowledgeDraft, setKnowledgeDraft] = useState<KnowledgeDraft>({
    title: '',
    category: '',
  });
  const knowledgeFormRef = useRef<HTMLDivElement>(null);

  // Usage + upgrade modal — single source of truth so banners + strip share it.
  const usageQuery = useBotUsage();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // ── Default-tab logic ────────────────────────────────────────────────────
  // Mirror the same setup/draft derivation BotHealthCard uses. React Query
  // dedupes the `useBotKnowledge` request, so calling it here is free.
  const knowledgeQuery = useBotKnowledge();
  const { setupIncomplete, hasDrafts } = useMemo(() => {
    const byKey = new Map<string, BotKnowledge>();
    let draftCount = 0;
    for (const entry of knowledgeQuery.data ?? []) {
      if (entry.onboardingKey) byKey.set(entry.onboardingKey, entry);
      if (entry.status === 'draft') draftCount += 1;
    }
    const liveCount = ONBOARDING_PROMPTS.reduce(
      (n, p) => (byKey.get(p.key)?.status === 'approved' ? n + 1 : n),
      0,
    );
    return {
      setupIncomplete: liveCount < ONBOARDING_PROMPTS.length,
      hasDrafts: draftCount > 0,
    };
  }, [knowledgeQuery.data]);

  const computeDefaultTab = (): ChatbotTabId => {
    if (setupIncomplete) return 'setup';
    if (hasDrafts) return 'knowledge';
    return 'knowledge';
  };

  // ── Active tab + URL hash deep-linking ───────────────────────────────────
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ChatbotTabId>(() => {
    // On first mount, honour any hash the user landed with.
    const fromHash = hashToTab(location.hash);
    return fromHash ?? 'knowledge';
  });

  // Once the knowledge query resolves we may want to refine the default tab.
  // We do this exactly once and only if the URL has no hash AND the tab is
  // still on its initial value (so we don't override a user click).
  const initialDefaultApplied = useRef(false);
  useEffect(() => {
    if (initialDefaultApplied.current) return;
    if (knowledgeQuery.isLoading) return;
    if (location.hash) {
      // Hash wins — mark default as "applied" so it doesn't stomp later.
      initialDefaultApplied.current = true;
      return;
    }
    initialDefaultApplied.current = true;
    setActiveTab(computeDefaultTab());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeQuery.isLoading]);

  // Watch hash changes after mount (e.g. Guide page CTA → `/chatbot#gaps`).
  useEffect(() => {
    const fromHash = hashToTab(location.hash);
    if (fromHash) setActiveTab(fromHash);
  }, [location.hash]);

  // Reflect tab changes in the URL hash so refreshes and shared links work.
  // We use `replace` so back/forward doesn't fill up history with tab clicks.
  const handleTabChange = (next: ChatbotTabId) => {
    // Any user-driven tab change locks in the default; the async-default
    // effect below will no-op so a slow knowledge query doesn't overwrite
    // the tab the user just chose.
    initialDefaultApplied.current = true;
    setActiveTab(next);
    // Map `insights` back to `#gaps` for parity with the existing Guide CTAs.
    const hash = next === 'insights' ? 'gaps' : next;
    navigate(`${location.pathname}#${hash}`, { replace: true });
  };

  // Pre-fill the Add-knowledge form from a gap, then switch to the Knowledge
  // tab and scroll the form into view.
  const draftArticleFromGap = (gap: BotKnowledgeGap) => {
    setKnowledgeDraft({ title: gap.topic, category: '' });
    handleTabChange('knowledge');
    // Defer the scroll until after the tab content has rendered.
    window.setTimeout(() => {
      knowledgeFormRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 60);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Chatbot"
        description="Your customer-facing bot. Answers only from knowledge you've approved; escalates honestly when it can't."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to="/chatbot/guide">
                <BookOpen className="h-3.5 w-3.5" />
                Help &amp; Setup Guide
              </Link>
            </Button>
            {/* Embedding is the final, required step to put the bot on the
                tenant's site — surface it directly from the Chatbot page so
                it isn't buried under Developer. */}
            <Button size="sm" asChild>
              <Link to="/chatbot/embed">
                <Code2 className="h-3.5 w-3.5" />
                Embed on your website
              </Link>
            </Button>
          </div>
        }
      />

      {/* Conditional usage banner — non-BETA tenants only, status ≠ under.
          The component renders nothing for BETA / `under` tenants, so the
          conditional placement is just to avoid passing partial data. */}
      {usageQuery.data && (
        <UsageBanner
          usage={usageQuery.data}
          onUpgrade={() => setUpgradeOpen(true)}
        />
      )}

      {/* "Header block" — Bot Health + Plan strip stay above the tabs.
          Tight `space-y-3` so they read as a single unit; tabs below get
          more breathing room via the wrapper's `space-y-6`. */}
      <div className="space-y-3">
        <BotHealthCard onNavigate={handleTabChange} />
        <UsageStrip
          usage={usageQuery.data}
          isLoading={usageQuery.isLoading}
          onUpgrade={() => setUpgradeOpen(true)}
        />
      </div>

      {/* Tab strip — underline-style active indicator, border-bottom on the
          strip itself for the standard pattern used elsewhere in the app. */}
      <TabStrip
        active={activeTab}
        onChange={handleTabChange}
      />

      <TabPanel id="setup" active={activeTab}>
        <div data-section="setup">
          <OnboardingChecklistSection />
        </div>
      </TabPanel>

      <TabPanel id="knowledge" active={activeTab}>
        {/* `id="knowledge"` preserved for backwards-compat with deep-links. */}
        <div id="knowledge" className="scroll-mt-24">
          <KnowledgeBaseSection
            formRef={knowledgeFormRef}
            draft={knowledgeDraft}
            onDraftChange={setKnowledgeDraft}
            onJumpToSetup={() => handleTabChange('setup')}
          />
        </div>
      </TabPanel>

      <TabPanel id="insights" active={activeTab}>
        {/* `id="gaps"` preserved for backwards-compat with deep-links. */}
        <div id="gaps" className="scroll-mt-24">
          <KnowledgeGapsSection onDraftArticle={draftArticleFromGap} />
        </div>
        {/* Bot activity — audit + improvement signal for portal-channel
            chats the bot is handling. Placed below the gaps section so
            the loop reads top-to-bottom: missing topics → what the bot
            actually did → per-conversation deep-link for detail. */}
        <BotActivitySection />
      </TabPanel>

      <TabPanel id="test" active={activeTab}>
        <PreviewSection botName={botName} greeting={greeting} />
      </TabPanel>

      <TabPanel id="settings" active={activeTab}>
        <AppearanceSection />
        <BotSettingsSection
          botName={botName}
          setBotName={setBotName}
          greeting={greeting}
          setGreeting={setGreeting}
        />
      </TabPanel>

      {/* Upgrade modal — single instance, shared by all "Upgrade plan" CTAs. */}
      <UpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        recommendedTier={
          usageQuery.data?.plan.tier === 'STARTER'
            ? 'GROWTH'
            : usageQuery.data?.plan.tier === 'GROWTH'
              ? 'SCALE'
              : undefined
        }
      />
    </div>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────────────

/**
 * The page-level tab strip. A lightweight controlled implementation (the
 * ui-kit doesn't expose Tabs yet, so we render an ARIA-correct `tablist`
 * here rather than pull in a dependency). Visual style matches the inline
 * `PreviewViewToggle` pattern used elsewhere in the page: underline-style
 * indicator under the active tab, with a shared border-bottom.
 */
function TabStrip({
  active,
  onChange,
}: {
  active: ChatbotTabId;
  onChange: (next: ChatbotTabId) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Chatbot sections"
      className="flex items-center gap-1 overflow-x-auto border-b border-border"
    >
      {TAB_DEFS.map((tab) => {
        const Icon = tab.icon;
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={cn(
              'relative inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A single tab panel. We render the active one only — re-mounting on tab
 * change is acceptable here because the heavy data (`useBotKnowledge`,
 * `useBotUsage`, `useCustomers`) lives in React Query and is therefore
 * shared across renders. Local state inside each section (e.g. the gaps
 * analysis mutation result, the preview thread) is intentionally NOT
 * preserved across tab switches — switching away is a clean reset.
 */
function TabPanel({
  id,
  active,
  children,
}: {
  id: ChatbotTabId;
  active: ChatbotTabId;
  children: React.ReactNode;
}) {
  if (id !== active) return null;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      className="space-y-6"
    >
      {children}
    </div>
  );
}

// ─── Bot configuration (Settings tab) ───────────────────────────────────────

/**
 * Identity + Routing — extracted out of `ChatbotPage` so it can live in the
 * Settings tab without being inline noise. Behaviour is unchanged from the
 * original implementation; only the wrapper moved.
 */
function BotSettingsSection({
  botName,
  setBotName,
  greeting,
  setGreeting,
}: {
  botName: string;
  setBotName: (next: string) => void;
  greeting: string;
  setGreeting: (next: string) => void;
}) {
  return (
    <>
      <SectionHeader
        withDivider
        title="Bot configuration"
        description="Identity and routing — how the bot introduces itself and when it hands off."
      />
      <DashboardGrid columns={2}>
        <DashboardCard
          title="Identity"
          description="What the customer sees when the bot picks up."
          icon={Bot}
        >
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="bot-name">Bot name</Label>
              <Input
                id="bot-name"
                value={botName}
                onChange={(e) => setBotName(e.target.value)}
                placeholder="Bot display name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bot-greeting">Opening greeting</Label>
              <Input
                id="bot-greeting"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bot-handover">Handover message</Label>
              <Input
                id="bot-handover"
                defaultValue="Connecting you with a teammate now — one moment."
              />
            </div>
          </div>
        </DashboardCard>

        <DashboardCard
          title="Routing"
          description="When the bot should step aside for a human."
          icon={Workflow}
        >
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-foreground">
                  Hand over on intent
                </div>
                <div className="text-xs text-muted-foreground">
                  "talk to human", "refund", "manager"
                </div>
              </div>
              <Badge tone="success" withDot={false}>
                On
              </Badge>
            </li>
            <Separator />
            <li className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-foreground">
                  Hand over after 2 unresolved turns
                </div>
                <div className="text-xs text-muted-foreground">
                  Prevents customers from looping with the bot.
                </div>
              </div>
              <Badge tone="success" withDot={false}>
                On
              </Badge>
            </li>
            <Separator />
            <li className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-foreground">
                  Auto-create support request
                </div>
                <div className="text-xs text-muted-foreground">
                  When the customer reports a problem with a recent order.
                </div>
              </div>
              <Badge tone="warning" withDot={false}>
                Pending setup
              </Badge>
            </li>
          </ul>
        </DashboardCard>
      </DashboardGrid>
    </>
  );
}

/**
 * A small `?` button that deep-links to a section of the Setup Guide.
 * Rendered inline with the section title.
 */
function SectionHelp({ anchor, label = 'Open the guide' }: { anchor: string; label?: string }) {
  const navigate = useNavigate();
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      className="h-6 w-6 text-muted-foreground hover:text-foreground"
      onClick={() => navigate(`/chatbot/guide#${anchor}`)}
    >
      <HelpCircle className="h-3.5 w-3.5" />
    </Button>
  );
}

/** Composes a section title with the inline help icon. */
function sectionTitle(text: string, anchor: string) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{text}</span>
      <SectionHelp anchor={anchor} />
    </span>
  );
}

// ─── Onboarding checklist ───────────────────────────────────────────────────

/**
 * The KB onboarding checklist — a curated set of standard prompts every new
 * tenant should answer before pointing the bot at customers (business hours,
 * how to contact them, return policy, etc.). Each answered prompt becomes a
 * `BotKnowledge` entry tagged with the prompt's `onboardingKey` and starts
 * its life as `draft`; nothing reaches the bot until the tenant approves it.
 *
 * State per prompt:
 *  - "not_started" — no entry with this `onboardingKey` yet. Tenant types an
 *    answer and clicks "Save draft" → creates a draft entry.
 *  - "draft" — entry exists but is still a draft. Tenant can edit it and
 *    "Approve" to make it live.
 *  - "live" — entry is approved. Shown collapsed with a checkmark; can be
 *    re-edited (which sends it back to a save that updates content only;
 *    re-approval is not needed for the existing approved entry — the API
 *    only updates fields supplied).
 */
function OnboardingChecklistSection() {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();
  const query = useBotKnowledge();
  const knowledgeKey = ['bot-knowledge', currentTenant.id];

  // Tenant's registered widget domains — drives the per-domain link fields
  // shown on each essential. Fetched once here, shared by every row.
  const domainsQuery = useQuery({
    queryKey: ['bot-domains', currentTenant.id],
    queryFn: getBotDomains,
    staleTime: 5 * 60_000,
  });

  // Index the existing KB by onboardingKey so each prompt can find its entry
  // in O(1). Entries with no `onboardingKey` (manually added) are ignored.
  const byKey = new Map<string, BotKnowledge>();
  for (const entry of query.data ?? []) {
    if (entry.onboardingKey) byKey.set(entry.onboardingKey, entry);
  }

  const liveCount = ONBOARDING_PROMPTS.reduce(
    (n, p) => (byKey.get(p.key)?.status === 'approved' ? n + 1 : n),
    0,
  );
  const total = ONBOARDING_PROMPTS.length;
  const pct = total === 0 ? 0 : Math.round((liveCount / total) * 100);

  // Collapse to a one-line "Setup complete ✓" row once every essential is
  // live — the section stays present for re-entry but stops dominating the
  // page. A small "Show details" toggle re-opens the full checklist.
  const isComplete = liveCount === total && total > 0;
  const [expanded, setExpanded] = useState(!isComplete);

  // If the tenant later un-approves an entry, re-open automatically.
  useEffect(() => {
    if (!isComplete) setExpanded(true);
  }, [isComplete]);

  if (isComplete && !expanded) {
    return (
      <>
        <SectionHeader
          withDivider
          title={sectionTitle('Set up your bot', 'quick-start')}
          description="The essentials customers always ask — answered."
        />
        <div className="flex items-center justify-between gap-3 rounded-lg border border-success/30 bg-success/5 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-success" />
            <span className="font-medium text-foreground">
              Setup complete
            </span>
            <span className="text-xs text-muted-foreground">
              All {total} essentials are approved and live.
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(true)}
          >
            Show details
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <SectionHeader
        withDivider
        title={sectionTitle('Set up your bot', 'quick-start')}
        description="Answer these 10 essentials customers always ask. Each answer becomes a draft entry — approve it to let the bot use it."
      />

      <DashboardCard
        icon={ClipboardList}
        title="Onboarding checklist"
        description="The fastest way to get your bot ready for customers."
        headerActions={
          <Badge tone={liveCount === total ? 'success' : 'info'} withDot={false}>
            {liveCount} of {total} essentials live
          </Badge>
        }
      >
        <div className="space-y-4">
          {/* Progress bar — `liveCount / total` of approved entries. */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Coverage</span>
              <span className="font-medium text-foreground">{pct}%</span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={liveCount}
              aria-valuemin={0}
              aria-valuemax={total}
              aria-label="Onboarding coverage"
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
            >
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  liveCount === total ? 'bg-success' : 'bg-primary',
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {query.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <ul className="space-y-2.5">
              {ONBOARDING_PROMPTS.map((prompt) => (
                <OnboardingChecklistRow
                  key={prompt.key}
                  prompt={prompt}
                  entry={byKey.get(prompt.key)}
                  domains={domainsQuery.data}
                  invalidate={() =>
                    queryClient.invalidateQueries({ queryKey: knowledgeKey })
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </DashboardCard>
    </>
  );
}

/**
 * One row of the onboarding checklist. Owns its own draft text + mutations
 * so a save / approve on one prompt does not disable the others.
 */
function OnboardingChecklistRow({
  prompt,
  entry,
  domains,
  invalidate,
}: {
  prompt: OnboardingPrompt;
  entry: BotKnowledge | undefined;
  domains?: BotDomains;
  invalidate: () => void;
}) {
  // Derived state — what UI mode this row is in.
  const state: 'not_started' | 'draft' | 'live' = !entry
    ? 'not_started'
    : entry.status === 'approved'
      ? 'live'
      : 'draft';

  // Editable answer text. Seeded from the entry when one exists. We keep an
  // `expanded` flag for the "live" state so the row stays compact by default
  // but can be opened to edit.
  const [answer, setAnswer] = useState(entry?.content ?? '');
  const [links, setLinks] = useState<BotKnowledgeLink[]>(entry?.links ?? []);
  const [expanded, setExpanded] = useState(false);

  // If the underlying entry changes (e.g. invalidate refetched it after an
  // approve), re-sync the local answer + links when NOT actively editing.
  useEffect(() => {
    if (!expanded) {
      setAnswer(entry?.content ?? '');
      setLinks(entry?.links ?? []);
    }
  }, [entry?.id, entry?.content, entry?.updatedAt, expanded]);

  const createMutation = useMutation({
    mutationFn: createBotKnowledge,
    onSuccess: () => {
      toast.success(`Saved draft: ${prompt.question}`);
      invalidate();
    },
    onError: () => toast.error("Couldn't save that draft — try again."),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; content: string; links: BotKnowledgeLink[] }) =>
      // Essentials always belong in the portal FAQ — keep showOnPortal set
      // so editing one never drops it out of the FAQ.
      updateBotKnowledge(input.id, {
        content: input.content,
        links: input.links,
        showOnPortal: true,
      }),
    onSuccess: () => {
      toast.success('Answer updated');
      setExpanded(false);
      invalidate();
    },
    onError: () => toast.error("Couldn't save that change — try again."),
  });

  const approveMutation = useMutation({
    mutationFn: approveBotKnowledge,
    onSuccess: () => {
      toast.success(`Live: ${prompt.question}`);
      invalidate();
    },
    onError: () => toast.error("Couldn't approve that entry — try again."),
  });

  const isBusy =
    createMutation.isPending ||
    updateMutation.isPending ||
    approveMutation.isPending;
  const canSave = answer.trim().length > 0;

  const handleCreate = () => {
    if (!canSave || isBusy) return;
    createMutation.mutate({
      title: prompt.question,
      content: answer.trim(),
      category: prompt.category,
      onboardingKey: prompt.key,
      links: cleanLinks(links),
      // Essentials are the portal FAQ — expose on create (visible once approved).
      showOnPortal: true,
    });
  };

  const handleUpdate = () => {
    if (!entry || !canSave || isBusy) return;
    updateMutation.mutate({ id: entry.id, content: answer.trim(), links: cleanLinks(links) });
  };

  const handleApprove = () => {
    if (!entry || isBusy) return;
    approveMutation.mutate(entry.id);
  };

  // ── Live (approved): compact row with a checkmark; expand to edit. ────
  if (state === 'live' && entry) {
    return (
      <li className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2.5">
            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {prompt.question}
                </span>
                <Badge tone="success" withDot={false}>
                  Live
                </Badge>
                <span className="text-[11px] text-muted-foreground">
                  {prompt.category}
                </span>
              </div>
              {!expanded && (
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                  {entry.content}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                expanded && 'rotate-180',
              )}
            />
            {expanded ? 'Close' : 'Edit'}
          </Button>
        </div>
        {expanded && (
          <div className="mt-3 space-y-3">
            <textarea
              id={`onb-${prompt.key}`}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={3}
              className="min-h-20 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-elev-1 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <LinkEditor links={links} onChange={setLinks} domains={domains} />
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={isBusy}
                onClick={() => {
                  setAnswer(entry.content);
                  setLinks(entry.links ?? []);
                  setExpanded(false);
                }}
              >
                Cancel
              </Button>
              <Button size="sm" disabled={!canSave || isBusy} onClick={handleUpdate}>
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </li>
    );
  }

  // ── Draft: prefilled answer, Save + Approve buttons. ──────────────────
  // ── Not started: empty textarea + Save draft button. ──────────────────
  return (
    <li className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
            state === 'draft'
              ? 'bg-warning/15 text-warning'
              : 'bg-muted text-muted-foreground',
          )}
          aria-hidden="true"
        >
          {state === 'draft' ? <PenLine className="h-3 w-3" /> : '•'}
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">
              {prompt.question}
            </span>
            {state === 'draft' ? (
              <Badge tone="warning" withDot={false}>
                Draft
              </Badge>
            ) : (
              <Badge tone="neutral" withDot={false}>
                Not started
              </Badge>
            )}
            <span className="text-[11px] text-muted-foreground">
              {prompt.category}
            </span>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {prompt.help}
          </p>
          <textarea
            id={`onb-${prompt.key}`}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={3}
            placeholder="Your answer to this question…"
            className="min-h-20 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-elev-1 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <LinkEditor links={links} onChange={setLinks} domains={domains} />
          <div className="flex items-center justify-end gap-2">
            {state === 'draft' && entry ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canSave || isBusy}
                  onClick={handleUpdate}
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  disabled={isBusy}
                  onClick={handleApprove}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {approveMutation.isPending ? 'Approving…' : 'Approve'}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                disabled={!canSave || isBusy}
                onClick={handleCreate}
              >
                <Plus className="h-3.5 w-3.5" />
                {createMutation.isPending ? 'Saving…' : 'Save draft'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

// ─── Knowledge gaps ─────────────────────────────────────────────────────────

/**
 * The self-improving knowledge base — questions the customer-facing bot
 * couldn't answer, clustered into topics. It does NOT auto-load: the runtime
 * runs a slow LLM clustering pass, so the tenant triggers it on demand with
 * "Analyze gaps". Each result offers "Draft an article", which pre-fills the
 * Add-knowledge form in the Knowledge base section below.
 */
function KnowledgeGapsSection({
  onDraftArticle,
}: {
  onDraftArticle: (gap: BotKnowledgeGap) => void;
}) {
  // Cache the analysis result on the React Query cache (key: `bot-gaps`) so
  // BotHealthCard can read it without re-running the slow clustering pass.
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const analysis = useMutation({
    mutationFn: getBotGaps,
    onSuccess: (data) => {
      queryClient.setQueryData(['bot-gaps', currentTenant.id], data);
    },
  });
  const gaps = analysis.data;

  return (
    <>
      <SectionHeader
        withDivider
        title={sectionTitle(
          "What customers ask that we can't answer yet",
          'gaps',
        )}
        description="See which topics customers asked about that the bot couldn't answer."
      />

      <DashboardCard
        icon={Lightbulb}
        title="What the bot is missing"
        description="This is the bot telling you what it doesn't know yet."
        headerActions={
          <Button
            size="sm"
            variant={gaps ? 'outline' : 'default'}
            onClick={() => analysis.mutate()}
            disabled={analysis.isPending}
          >
            <Sparkles className="h-3.5 w-3.5" />
            {analysis.isPending
              ? 'Analysing…'
              : gaps
                ? 'Re-analyze'
                : 'Analyze gaps'}
          </Button>
        }
      >
        {analysis.isPending ? (
          <div className="space-y-3">
            <p className="text-xs leading-relaxed text-muted-foreground">
              Analysing what the bot couldn't answer… this clusters customer
              questions with a model, so it can take a few moments.
            </p>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : analysis.isError ? (
          <EmptyState
            title="We couldn't analyze gaps just yet"
            description="Try again in a moment — if it persists, the runtime might be unreachable."
          />
        ) : !gaps ? (
          <p className="text-xs leading-relaxed text-muted-foreground">
            No unanswered questions to review yet. We'll log questions here
            that customers asked but your bot couldn't answer — run an
            analysis to cluster them into topics, then write the answer for
            each.
          </p>
        ) : gaps.length === 0 ? (
          <EmptyState
            icon={CheckCircle2}
            title="Nothing unanswered"
            description="When the bot can't answer something, it'll show up here so you can write the answer."
          />
        ) : (
          <div className="space-y-3">
            {gaps.map((gap) => (
              <KnowledgeGapRow
                key={gap.topic}
                gap={gap}
                onDraftArticle={() => onDraftArticle(gap)}
              />
            ))}
          </div>
        )}
      </DashboardCard>
    </>
  );
}

function KnowledgeGapRow({
  gap,
  onDraftArticle,
}: {
  gap: BotKnowledgeGap;
  onDraftArticle: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              {gap.topic}
            </span>
            <Badge tone="warning" withDot={false}>
              {gap.count} {gap.count === 1 ? 'customer' : 'customers'} asked
            </Badge>
          </div>
          <ul className="mt-2 space-y-1">
            {gap.examples.map((example) => (
              <li
                key={example}
                className="text-xs leading-relaxed text-muted-foreground"
              >
                “{example}”
              </li>
            ))}
          </ul>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={onDraftArticle}
        >
          <PenLine className="h-3.5 w-3.5" />
          Write the answer
        </Button>
      </div>
    </div>
  );
}

// ─── Knowledge base ─────────────────────────────────────────────────────────

/** Trim links + their per-domain URLs and drop blanks (label + primary url required). */
function cleanLinks(links: BotKnowledgeLink[]): BotKnowledgeLink[] {
  return links
    .map((l) => {
      const domainUrls = (l.domainUrls ?? [])
        .map((d) => ({ domain: d.domain.trim(), url: d.url.trim() }))
        .filter((d) => d.domain && d.url);
      const link: BotKnowledgeLink = { label: l.label.trim(), url: l.url.trim() };
      if (domainUrls.length) link.domainUrls = domainUrls;
      return link;
    })
    .filter((l) => l.label && l.url);
}

function KnowledgeBaseSection({
  formRef,
  draft,
  onDraftChange,
  onJumpToSetup,
}: {
  formRef: React.RefObject<HTMLDivElement>;
  draft: KnowledgeDraft;
  onDraftChange: (draft: KnowledgeDraft) => void;
  /**
   * Switches the page to the Setup tab. Passed from `ChatbotPage` because
   * the section itself doesn't know about the tab state; it just needs a
   * way to route the empty-state "Jump to Setup" CTA.
   */
  onJumpToSetup: () => void;
}) {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();
  const query = useBotKnowledge();
  const knowledgeKey = ['bot-knowledge', currentTenant.id];

  // `title`/`category` are lifted to `ChatbotPage` so the Knowledge gaps
  // section can pre-fill them via "Draft an article". `content` stays local.
  const { title, category } = draft;
  const setTitle = (value: string) => onDraftChange({ ...draft, title: value });
  const setCategory = (value: string) =>
    onDraftChange({ ...draft, category: value });
  const [content, setContent] = useState('');
  const [links, setLinks] = useState<BotKnowledgeLink[]>([]);
  const [showOnPortal, setShowOnPortal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // The tenant's registered widget domains — drives the per-domain link
  // editor. Stale-cached; domains rarely change.
  const domainsQuery = useQuery({
    queryKey: ['bot-domains', currentTenant.id],
    queryFn: getBotDomains,
    staleTime: 5 * 60_000,
  });

  // When a gap pre-fills the title, focus the field so the tenant can edit.
  useEffect(() => {
    if (title) titleRef.current?.focus();
  }, [title]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: knowledgeKey });

  const createMutation = useMutation({
    mutationFn: createBotKnowledge,
    onSuccess: () => {
      onDraftChange({ title: '', category: '' });
      setContent('');
      setLinks([]);
      setShowOnPortal(false);
      invalidate();
    },
  });

  const approveMutation = useMutation({
    mutationFn: approveBotKnowledge,
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteBotKnowledge,
    onSuccess: invalidate,
  });

  const editMutation = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Parameters<typeof updateBotKnowledge>[1];
    }) => updateBotKnowledge(id, patch),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
  });

  const canSubmit = title.trim().length > 0 && content.trim().length > 0;
  const isBusy =
    createMutation.isPending ||
    approveMutation.isPending ||
    deleteMutation.isPending;

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || createMutation.isPending) return;
    createMutation.mutate({
      title: title.trim(),
      content: content.trim(),
      category: category.trim() || undefined,
      links: cleanLinks(links),
      showOnPortal,
    });
  };

  const approvedCount =
    query.data?.filter((k) => k.status === 'approved').length ?? 0;

  // The "Jump to Setup" CTA used to scroll inside a single page; now it just
  // asks the parent to switch tabs. Scrolling falls out of the tab change.
  const scrollToSetup = () => onJumpToSetup();

  return (
    <>
      <SectionHeader
        withDivider
        title={sectionTitle('What the bot can answer', 'writing-knowledge')}
        description="The bot answers customers only from APPROVED entries below — draft entries stay invisible to it until you approve them."
      />

      <DashboardGrid columns={2}>
        <div ref={formRef} className="lg:col-span-1">
          <DashboardCard
            title="Add a new answer"
            description="New entries start as a draft. Approve them to let the bot use them."
            icon={Plus}
          >
          <form className="space-y-3" onSubmit={handleCreate}>
            <div className="space-y-1.5">
              <Label htmlFor="kb-title">Title</Label>
              <Input
                id="kb-title"
                ref={titleRef}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Delivery timelines"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="kb-category">Category (optional)</Label>
              <Input
                id="kb-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Orders & shipping"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="kb-content">Answer content</Label>
              <textarea
                id="kb-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                placeholder="The exact information the bot may share with customers…"
                className="min-h-24 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-elev-1 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Reference links — shown on the portal FAQ AND in the bot reply.
                Per-domain when the tenant's widget runs on several domains. */}
            <LinkEditor links={links} onChange={setLinks} domains={domainsQuery.data} />

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={showOnPortal}
                onChange={(e) => setShowOnPortal(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input"
              />
              <span>
                Show on portal FAQ
                <span className="block text-[11.5px] text-muted-foreground">
                  Appears in the public FAQ once approved. The bot already uses every approved entry.
                </span>
              </span>
            </label>

            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || createMutation.isPending}
            >
              <Plus className="h-3.5 w-3.5" />
              {createMutation.isPending ? 'Adding…' : 'Add knowledge'}
            </Button>
          </form>
          </DashboardCard>
        </div>

        <DashboardCard
          title="What the bot knows"
          description="Only approved entries are used to answer customers."
          icon={ShieldCheck}
          headerActions={
            <Badge tone={approvedCount > 0 ? 'success' : 'warning'} withDot={false}>
              {approvedCount} approved
            </Badge>
          }
        >
          <p className="text-xs leading-relaxed text-muted-foreground">
            The customer-facing bot is constrained: it never improvises. Every
            reply is grounded in an approved entry, and when it cannot find one
            it hands the conversation to a human. Keep drafts under review until
            the wording is exactly what you want customers to read.
          </p>
        </DashboardCard>
      </DashboardGrid>

      <DashboardCard flushBody icon={BookCheck} title="Knowledge entries">
        <QueryStateGuard
          data={query.data}
          isLoading={query.isLoading}
          isError={query.isError}
          emptyTitle="No knowledge yet"
          emptyDescription="Start by setting up your bot above — the essential answers go here."
          emptyAction={
            <Button size="sm" variant="outline" onClick={scrollToSetup}>
              Jump to Setup
            </Button>
          }
        >
          {(entries) => (
            <TableShell className="rounded-none border-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entry</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <KnowledgeRow
                      key={entry.id}
                      entry={entry}
                      disabled={isBusy}
                      domains={domainsQuery.data}
                      approving={
                        approveMutation.isPending &&
                        approveMutation.variables === entry.id
                      }
                      deleting={
                        deleteMutation.isPending &&
                        deleteMutation.variables === entry.id
                      }
                      editing={editingId === entry.id}
                      saving={
                        editMutation.isPending &&
                        editMutation.variables?.id === entry.id
                      }
                      onApprove={() => approveMutation.mutate(entry.id)}
                      onDelete={() => deleteMutation.mutate(entry.id)}
                      onStartEdit={() => setEditingId(entry.id)}
                      onCancelEdit={() => setEditingId(null)}
                      onSave={(patch) => editMutation.mutate({ id: entry.id, patch })}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </>
  );
}

function KnowledgeRow({
  entry,
  disabled,
  domains,
  approving,
  deleting,
  editing,
  saving,
  onApprove,
  onDelete,
  onStartEdit,
  onCancelEdit,
  onSave,
}: {
  entry: BotKnowledge;
  disabled: boolean;
  domains?: BotDomains;
  approving: boolean;
  deleting: boolean;
  editing: boolean;
  saving: boolean;
  onApprove: () => void;
  onDelete: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (patch: {
    title: string;
    content: string;
    category?: string;
    links: BotKnowledgeLink[];
    showOnPortal: boolean;
  }) => void;
}) {
  const [title, setTitle] = useState(entry.title);
  const [content, setContent] = useState(entry.content);
  const [category, setCategory] = useState(entry.category ?? '');
  const [links, setLinks] = useState<BotKnowledgeLink[]>(entry.links ?? []);
  const [showOnPortal, setShowOnPortal] = useState(entry.showOnPortal ?? false);

  // Re-seed the edit form from the entry each time we (re)open the editor.
  useEffect(() => {
    if (editing) {
      setTitle(entry.title);
      setContent(entry.content);
      setCategory(entry.category ?? '');
      setLinks(entry.links ?? []);
      setShowOnPortal(entry.showOnPortal ?? false);
    }
  }, [editing, entry.id, entry.updatedAt]);

  const save = () => {
    if (!title.trim() || !content.trim()) return;
    onSave({
      title: title.trim(),
      content: content.trim(),
      category: category.trim() || undefined,
      links: cleanLinks(links),
      showOnPortal,
    });
  };

  return (
    <>
      <TableRow>
        <TableCell className="max-w-md">
          <div className="text-sm font-medium text-foreground">{entry.title}</div>
          <div className="line-clamp-2 text-xs text-muted-foreground">
            {entry.content}
          </div>
          {entry.suggestedFromRequestId && (
            <Badge tone="info" withDot={false} className="mt-1.5">
              <Sparkles className="h-3 w-3" />
              AI-drafted from a resolved request
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-sm text-foreground">
          {entry.category ?? (
            <span className="text-muted-foreground">Uncategorised</span>
          )}
        </TableCell>
        <TableCell>
          <StatusBadge
            status={entry.status}
            variants={botKnowledgeStatusVariants}
          />
          {entry.status === 'approved' && entry.approvedBy && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              by {entry.approvedBy}
            </div>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {relativeTime(entry.updatedAt)}
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-1.5">
            {entry.status === 'draft' && (
              <Button
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={onApprove}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {approving ? 'Approving…' : 'Approve'}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Edit ${entry.title}`}
              disabled={disabled}
              onClick={editing ? onCancelEdit : onStartEdit}
            >
              <PenLine className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Delete ${entry.title}`}
              disabled={disabled}
              onClick={onDelete}
            >
              <Trash2
                className={cn(
                  'h-4 w-4',
                  deleting ? 'text-muted-foreground' : 'text-destructive',
                )}
              />
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {editing && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/30">
            <div className="space-y-3 p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`edit-title-${entry.id}`}>Title</Label>
                  <Input
                    id={`edit-title-${entry.id}`}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`edit-cat-${entry.id}`}>Category (optional)</Label>
                  <Input
                    id={`edit-cat-${entry.id}`}
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`edit-content-${entry.id}`}>Answer content</Label>
                <textarea
                  id={`edit-content-${entry.id}`}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={3}
                  className="min-h-20 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-elev-1 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <LinkEditor links={links} onChange={setLinks} domains={domains} />

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showOnPortal}
                  onChange={(e) => setShowOnPortal(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-input"
                />
                <span>Show on portal FAQ</span>
              </label>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  disabled={saving || !title.trim() || !content.trim()}
                  onClick={save}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button size="sm" variant="ghost" disabled={saving} onClick={onCancelEdit}>
                  Cancel
                </Button>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── Preview ────────────────────────────────────────────────────────────────

interface PreviewMessage {
  id: string;
  role: 'user' | 'bot';
  content: string;
  meta?: Pick<
    BotPreviewReply,
    'grounded' | 'handoff' | 'sources' | 'requestRaised'
  >;
}

const PREVIEW_PROMPTS = [
  'What are your business hours?',
  'How long does delivery take?',
  'Can I return an item?',
  'Do you offer a payment plan for a wedding venue?',
];

type PreviewView = 'customer' | 'agent';

function PreviewSection({
  botName,
  greeting,
}: {
  botName: string;
  greeting: string;
}) {
  const [view, setView] = useState<PreviewView>('customer');
  const [customerId, setCustomerId] = useState('');
  const [messages, setMessages] = useState<PreviewMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  const customersQuery = useCustomers();
  const customers = customersQuery.data ?? [];
  const selectedCustomer = customers.find((c) => c.id === customerId);
  // Empty string = "Anonymous visitor" — no customer grounding.
  const previewCustomerId = customerId || undefined;

  useEffect(() => {
    threadRef.current?.scrollTo({
      top: threadRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, isThinking]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isThinking) return;

    const history = messages.map((m) => ({
      role: m.role === 'user' ? ('user' as const) : ('assistant' as const),
      content: m.content,
    }));
    const userMessage: PreviewMessage = {
      id: `pm_${Date.now()}`,
      role: 'user',
      content: trimmed,
    };
    setMessages((prev) => [...prev, userMessage]);
    setDraft('');
    setIsThinking(true);

    try {
      const reply = await previewBot(trimmed, history, previewCustomerId);
      setMessages((prev) => [
        ...prev,
        {
          id: `pm_${Date.now()}_bot`,
          role: 'bot',
          content: reply.reply,
          meta: {
            grounded: reply.grounded,
            handoff: reply.handoff,
            sources: reply.sources,
            requestRaised: reply.requestRaised,
          },
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `pm_${Date.now()}_err`,
          role: 'bot',
          content:
            "Something went wrong reaching the bot. Try again in a moment.",
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <>
      <SectionHeader
        withDivider
        title={sectionTitle('Try it', 'quick-start')}
        description="Test the bot exactly as a customer would. Replies here are not sent to anyone."
      />

      <DashboardCard
        flushBody
        icon={Bot}
        title={view === 'customer' ? 'Customer view' : 'Agent view'}
        headerActions={
          <div className="flex items-center gap-2">
            <PreviewCustomerSelect
              value={customerId}
              onChange={(next) => {
                setCustomerId(next);
                setMessages([]);
              }}
              customers={customers}
              disabled={isThinking || customersQuery.isLoading}
            />
            <PreviewViewToggle value={view} onChange={setView} />
            {view === 'agent' && messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMessages([])}
                disabled={isThinking}
              >
                Reset
              </Button>
            )}
          </div>
        }
      >
        <p className="border-b border-border px-5 py-3 text-xs leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">Customer view</span> —
          exactly what your customers see; demo this to your beta customer.{' '}
          <span className="font-medium text-foreground">Agent view</span> —
          a diagnostic preview that exposes sources and handoff for your team.
          {selectedCustomer ? (
            <>
              {' '}
              Previewing as{' '}
              <span className="font-medium text-foreground">
                {selectedCustomer.name}
              </span>
              {' '}— the bot can answer this customer's account questions and
              raise support requests for them.
            </>
          ) : (
            <>
              {' '}
              Previewing as an{' '}
              <span className="font-medium text-foreground">
                anonymous visitor
              </span>
              {' '}— pick a customer above to ground the bot on their 360.
            </>
          )}
        </p>

        {view === 'customer' ? (
          <div className="flex justify-center bg-muted/30 px-5 py-6">
            <CustomerChatWidget
              key={customerId || 'anon'}
              botName={botName}
              greeting={greeting}
              customerId={previewCustomerId}
            />
          </div>
        ) : (
          <>
            <div
              ref={threadRef}
              className="max-h-[26rem] min-h-[16rem] overflow-y-auto px-5 py-4"
            >
              {messages.length === 0 ? (
                <PreviewWelcome onPick={send} disabled={isThinking} />
              ) : (
                <div className="flex flex-col gap-3">
                  {messages.map((message) => (
                    <PreviewRow key={message.id} message={message} />
                  ))}
                  {isThinking && <PreviewThinking />}
                </div>
              )}
            </div>

            <form
              className="border-t border-border p-3"
              onSubmit={(e) => {
                e.preventDefault();
                void send(draft);
              }}
            >
              <div className="flex items-end gap-2">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send(draft);
                    }
                  }}
                  rows={1}
                  placeholder="Type a message as a customer…"
                  className="max-h-32 min-h-9 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm shadow-elev-1 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!draft.trim() || isThinking}
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-1.5 px-1 text-[11px] text-muted-foreground">
                The bot answers only from approved knowledge — when it can't, it
                escalates to a human.
              </p>
            </form>
          </>
        )}
      </DashboardCard>
    </>
  );
}

function PreviewCustomerSelect({
  value,
  onChange,
  customers,
  disabled,
}: {
  value: string;
  onChange: (customerId: string) => void;
  customers: Customer[];
  disabled: boolean;
}) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="sr-only">Previewing as</span>
      <UserRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Previewing as customer"
        className="rounded-md border border-input bg-background px-2 py-1 text-xs font-medium text-foreground shadow-elev-1 focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="">Anonymous visitor</option>
        {customers.map((customer) => (
          <option key={customer.id} value={customer.id}>
            {customer.name}
            {customer.company ? ` · ${customer.company}` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function PreviewViewToggle({
  value,
  onChange,
}: {
  value: PreviewView;
  onChange: (view: PreviewView) => void;
}) {
  const options: Array<{ id: PreviewView; label: string }> = [
    { id: 'customer', label: 'Customer view' },
    { id: 'agent', label: 'Agent view' },
  ];
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/50 p-0.5">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          aria-pressed={value === option.id}
          className={cn(
            'rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors',
            value === option.id
              ? 'bg-card text-foreground shadow-elev-1'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function PreviewWelcome({
  onPick,
  disabled,
}: {
  onPick: (text: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-border bg-muted/40 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Bot className="h-4 w-4 text-primary" />
          Try the bot as a customer
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
          Ask something covered by an approved entry to see a grounded reply
          with its sources — or ask something off-topic to see the bot hand off
          to a human.
        </p>
      </div>
      <div className="space-y-1.5">
        <p className="px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Try asking
        </p>
        {PREVIEW_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            type="button"
            disabled={disabled}
            onClick={() => onPick(prompt)}
            className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 text-left text-sm text-foreground transition-colors hover:border-foreground/20 hover:bg-muted/50 disabled:pointer-events-none disabled:opacity-50"
          >
            {prompt}
            <Send className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}

function PreviewRow({ message }: { message: PreviewMessage }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[85%] items-start gap-2">
          <div className="whitespace-pre-line rounded-lg rounded-br-sm bg-primary/10 px-3 py-2 text-sm text-foreground">
            {message.content}
          </div>
          <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <UserRound className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Bot className="h-3.5 w-3.5" />
      </span>
      <div className="flex max-w-[85%] flex-col gap-1.5">
        <div className="whitespace-pre-line rounded-lg rounded-tl-sm border border-border bg-card px-3 py-2 text-sm text-foreground">
          {message.content}
        </div>
        {message.meta?.handoff && (
          <Badge tone="warning" withDot={false}>
            Escalated to a human
          </Badge>
        )}
        {message.meta?.requestRaised && (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-medium text-foreground">
              <CheckCircle2 className="h-3 w-3 text-primary" />
              Raised:
            </span>
            <Badge tone="success" withDot={false}>
              {message.meta.requestRaised.reference}
            </Badge>
            <span>{message.meta.requestRaised.subject}</span>
          </div>
        )}
        {message.meta?.grounded && message.meta.sources.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-medium text-foreground">
              <BookCheck className="h-3 w-3 text-primary" />
              Answered from:
            </span>
            {message.meta.sources.map((source) => (
              <Badge key={source.id} tone="success" withDot={false}>
                {source.title}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewThinking() {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Bot className="h-3.5 w-3.5" />
      </span>
      <div className="flex items-center gap-1 rounded-lg rounded-tl-sm border border-border bg-card px-3 py-2.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/60',
            )}
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
    </div>
  );
}
